import React, { ReactNode } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { Alert, Button, Card, CardContent, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface PermissionGuardProps {
  children: ReactNode;
  requiredPlan?: string;
  requiredFeature?: string;
  requireAuth?: boolean;
  fallback?: ReactNode;
  redirectTo?: string;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  requiredPlan,
  requiredFeature,
  requireAuth = true,
  fallback,
  redirectTo
}) => {
  const { 
    isAuthenticated, 
    hasPlan, 
    hasFeature, 
    requiresUpgrade,
    currentSubscription 
  } = usePermissions();
  const navigate = useNavigate();

  // Vérification authentification
  if (requireAuth && !isAuthenticated) {
    if (redirectTo) {
      navigate(redirectTo);
      return null;
    }
    
    return fallback || (
      <Alert severity="warning">
        Vous devez être connecté pour accéder à cette section.
        <Button onClick={() => navigate('/login')} sx={{ ml: 1 }}>
          Se connecter
        </Button>
      </Alert>
    );
  }

  // Vérification plan requis
  if (requiredPlan && !hasPlan(requiredPlan)) {
    return fallback || (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" color="warning.main" gutterBottom>
            Abonnement requis
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Cette fonctionnalité nécessite un abonnement "{requiredPlan}".
          </Typography>
          {currentSubscription ? (
            <Typography variant="body2" mb={2}>
              Votre plan actuel : {currentSubscription.plan.name}
            </Typography>
          ) : (
            <Typography variant="body2" mb={2}>
              Aucun abonnement actif trouvé.
            </Typography>
          )}
          <Box>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/subscription/upgrade')}
              sx={{ mr: 1 }}
            >
              Mettre à niveau
            </Button>
            <Button 
              variant="outlined"
              onClick={() => navigate('/subscription/plans')}
            >
              Voir les plans
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Vérification feature requise
  if (requiredFeature && !hasFeature(requiredFeature)) {
    return fallback || (
      <Alert severity="info">
        Cette fonctionnalité n'est pas incluse dans votre plan actuel.
        <Button onClick={() => navigate('/subscription/upgrade')} sx={{ ml: 1 }}>
          Mettre à niveau
        </Button>
      </Alert>
    );
  }

  // Toutes les vérifications passées
  return <>{children}</>;
};

// Composant spécialisé pour FAQ
export const FAQPermissionGuard: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <PermissionGuard 
      requiredPlan="FAQ WhatsApp"
      fallback={
        <Card sx={{ mt: 2, textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              🤖 Service FAQ WhatsApp
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Automatisez vos réponses WhatsApp avec notre agent IA.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => window.location.href = '/subscription/plans'}
            >
              Souscrire au plan FAQ WhatsApp
            </Button>
          </CardContent>
        </Card>
      }
    >
      {children}
    </PermissionGuard>
  );
};

